"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect } from "react";
import { TerraLink } from "../../component/TerraLink/TerraLink";
import { darkTextureWrapper, colorSettingDetection, renderHeaderConditionally } from "../../utils/functions";
import { CommonDescription } from "../../component/CommonDescription/CommonDescription";
import { useScreenSize } from "../../utils/hooks/useScreenSize";
import isExternalLink from "../../utils/isExternalLink";
// import { FocalPointImageProps } from "../../component/FocusedImage/FocusedImageMethods"
import { ContentfulImageProps } from "../../component/ContentfulImage/ContentfulImageMethods";
// import { renderFocusedOrRegularImage } from "../../utils/renderFocusedOrRegularImage"
import { renderContentfulImage } from "../../utils/renderImage";
import { layoutHeaderClasses, layoutHeaderBigClasses, layoutSubheaderClasses, layoutEyebrowClasses } from "@/components/tailwindHelpers";
export type ImageListItem = {
  image?: ContentfulImageProps;
  header: string;
  description: string;
  destination?: string;
  link?: TerraLink;
};
const itemLinkClasses = "text-base font-semibold leading-relaxed tracking-normal no-underline text-blue-trmb gap-2 items-center";
export interface LayoutRelatedSubjects {
  eyebrow?: string;
  header?: string;
  headerElement?: "h1" | "h2";
  subheader?: string;
  description?: string;
  orientation?: "left" | "right";
  relatedListItemsTitle?: string;
  relatedListItems?: ImageListItem[];
  buttons?: TerraLink[];
  presetTheme?: "t2-theme-one" | "t2-theme-two" | "t2-theme-three" | "t2-theme-four" | "t2-theme-five" | "t2-theme-six" | "t2-theme-seven";
  isHeaderBig?: boolean;
  isWrapped?: boolean;
  componentImagePriority?: "high" | "auto" | "low";
  isLCP?: boolean;
}
export const TruncatedText = t0 => {
  const $ = _c(6);
  const {
    text,
    className
  } = t0;
  const [truncatedText, setTruncatedText] = useState(text);
  let t1;
  let t2;
  if ($[0] !== text) {
    t1 = () => {
      if (!text || typeof window === "undefined") {
        return;
      }
      let rafId = null;
      const handleResize = () => {
        if (rafId !== null) {
          return;
        }
        rafId = requestAnimationFrame(() => {
          const containerWidth = window.innerWidth;
          const charPerLine = Math.floor(containerWidth / 15);
          const maxWords = charPerLine * 2;
          const newText = text.length > maxWords ? `${text.substring(0, maxWords)}...` : text;
          setTruncatedText(newText);
          rafId = null;
        });
      };
      window.addEventListener("resize", handleResize, {
        passive: true
      });
      handleResize();
      return () => {
        window.removeEventListener("resize", handleResize);
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      };
    };
    t2 = [text];
    $[0] = text;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== className || $[4] !== truncatedText) {
    t3 = <span className={className}>{truncatedText}</span>;
    $[3] = className;
    $[4] = truncatedText;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
const ITEM_CLASSES = "flex pb-10 no-underline items-center last:pb-0";
const wrapImageListItem = (item: ImageListItem, index: number, children: React.ReactNode) => {
  if (!item.link?.label && item.destination !== undefined) {
    return <a key={index} className={`${ITEM_CLASSES} cursor-pointer group active:bg-blue-dnavy/20 rounded-lg transition-colors`} href={item?.destination} target={isExternalLink(item?.destination) ? "_blank" : "_self"} rel={isExternalLink(item?.destination) ? "noreferrer" : undefined} data-testid="related-subject-item" style={{
      WebkitTapHighlightColor: "transparent"
    }}>
        {children}
      </a>;
  } else {
    return <div key={index} className={ITEM_CLASSES} data-testid="related-subject-item">
        {children}
      </div>;
  }
};
export const LayoutRelatedSubjects = t0 => {
  const $ = _c(44);
  const {
    eyebrow,
    header,
    headerElement: t1,
    subheader,
    description,
    isHeaderBig,
    presetTheme,
    isWrapped,
    orientation,
    relatedListItemsTitle,
    relatedListItems,
    buttons,
    componentImagePriority,
    isLCP: t2
  } = t0;
  const headerElement = t1 === undefined ? "h2" : t1;
  const isLCP = t2 === undefined ? false : t2;
  const {
    width
  } = useScreenSize();
  const wrapperClasses = isWrapped ? "w-full px-5 lg:px-20 m-auto max-w-[1440px]" : "";
  const t3 = presetTheme || "";
  let t4;
  if ($[0] !== t3) {
    t4 = colorSettingDetection(t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const colorSetting = t4;
  const mainClasses = `py-[100px] ${presetTheme || ""} ${colorSetting} t2-lo-related-subjects--o-${orientation}`;
  const headerClasses = isHeaderBig ? layoutHeaderBigClasses : layoutHeaderClasses;
  const t5 = header || "";
  let t6;
  if ($[2] !== headerClasses || $[3] !== headerElement || $[4] !== t5) {
    t6 = renderHeaderConditionally(headerClasses, t5, headerElement);
    $[2] = headerClasses;
    $[3] = headerElement;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const finalHeader = t6;
  const t7 = presetTheme || "";
  let t8;
  if ($[6] !== t7) {
    t8 = darkTextureWrapper(t7);
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const componentStyles = t8;
  let t9;
  if ($[8] !== eyebrow) {
    t9 = eyebrow && <div className={layoutEyebrowClasses}>{eyebrow}</div>;
    $[8] = eyebrow;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== subheader) {
    t10 = subheader && <div className={layoutSubheaderClasses}>{subheader}</div>;
    $[10] = subheader;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== description) {
    t11 = description && <CommonDescription description={description} className="t2-lo-related-subjects__description t2-layout__description !mb-6" />;
    $[12] = description;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== buttons) {
    t12 = buttons && buttons.map(_temp);
    $[14] = buttons;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t12) {
    t13 = <div className="t2-layout__cta mt-10 max-sm:pb-20">{t12}</div>;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== finalHeader || $[19] !== t10 || $[20] !== t11 || $[21] !== t13 || $[22] !== t9) {
    t14 = <div className="basis-full max-w-full px-2.5 ml-0 min-lg:basis-1/2 min-lg:max-w-[50%]"><div>{t9}{finalHeader}{t10}{t11}{t13}</div></div>;
    $[18] = finalHeader;
    $[19] = t10;
    $[20] = t11;
    $[21] = t13;
    $[22] = t9;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== relatedListItemsTitle) {
    t15 = <div className="text-gray-m7 text-sm font-bold leading-5 tracking-wide uppercase pb-[50px] t2-dark:text-white">{relatedListItemsTitle}</div>;
    $[24] = relatedListItemsTitle;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== componentImagePriority || $[27] !== isLCP || $[28] !== relatedListItems || $[29] !== width) {
    t16 = relatedListItems && relatedListItems.map((item, index_0) => {
      const hasDestination = item?.destination !== undefined;
      const wrappedItem = wrapImageListItem(item, index_0, <>{item?.image?.src && <div className="w-[88px] h-[88px] shrink-0 mr-10 rounded-lg overflow-hidden max-sm:w-[72px] max-sm:h-[72px] max-sm:mr-5">{renderContentfulImage(item?.image?.src, item?.image?.alt || "", 88, 88, "rounded-lg w-full h-full object-cover", 95, item?.image?.focusArea, componentImagePriority, isLCP)}</div>}<div><h3 className="text-gray-m9 text-base font-semibold leading-snug t2-dark:text-white max-sm:hidden !pb-4 group-hover:underline">{item?.header || ""}</h3><div className="text-gray-m9 text-base font-light leading-snug w-full overflow-hidden text-ellipsis t2-dark:text-white max-sm:hidden">{item?.description}</div>{width < 600 && <div className={hasDestination ? "text-gray-m9 t2-dark:text-white group-hover:underline group-hover:decoration-blue-glass group-active:underline group-active:decoration-blue-glass" : "text-gray-m9 t2-dark:text-white"}><span className="text-base font-semibold leading-snug">{item?.header}</span>{" "}<TruncatedText text={item?.description} className="text-base font-light leading-snug" /></div>}{item?.link?.label && <div className="pt-5" data-testid="related-subject-item-link"><TerraLink {...item.link} linkType="text" isIcon={true} className={itemLinkClasses} /></div>}</div></>);
      return wrappedItem;
    });
    $[26] = componentImagePriority;
    $[27] = isLCP;
    $[28] = relatedListItems;
    $[29] = width;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t15 || $[32] !== t16) {
    t17 = <div className="basis-full max-w-full px-2.5 ml-0 max-lg:mt-10 min-lg:basis-[41.666%] min-lg:max-w-[41.666%] min-lg:ml-[8.333%]">{t15}{t16}</div>;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t14 || $[35] !== t17) {
    t18 = <div className="t2-layout__content"><div className="flex flex-wrap -mx-2.5">{t14}{t17}</div></div>;
    $[34] = t14;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t18 || $[38] !== wrapperClasses) {
    t19 = <div className={wrapperClasses}>{t18}</div>;
    $[37] = t18;
    $[38] = wrapperClasses;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== componentStyles || $[41] !== mainClasses || $[42] !== t19) {
    t20 = <div className={mainClasses} style={componentStyles} data-testid="layout-related-subjects">{t19}</div>;
    $[40] = componentStyles;
    $[41] = mainClasses;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  return t20;
};
function _temp(btn, index) {
  return <TerraLink key={index} {...btn} />;
}